import React, { useState, useEffect, useRef } from 'react';
import { Textarea } from './text-area';
import { cn } from '../../lib/utils';

const JsonMentionTextarea = ({
  value,
  onChange,
  questions, // Array of { id: string, text: string }
  placeholder,
  className,
  disabled,
  name,
  ...props
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const textareaRef = useRef(null);
  const suggestionsRef = useRef(null);
  const [activeMentionIndex, setActiveMentionIndex] = useState(-1);

  // Auto-resize functionality
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const lineHeight = 20; // Line height in pixels
    const minRows = 3; // Start with 3 lines
    const maxRows = 12; // Maximum 12 lines
    const minHeight = minRows * lineHeight;

    // Reset height to get accurate scroll height
    textarea.style.height = 'auto';

    // Calculate required rows based on scroll height
    const scrollHeight = textarea.scrollHeight;
    const calculatedRows = Math.ceil(scrollHeight / lineHeight);

    // Apply constraints: minimum 3 rows, maximum 12 rows
    const targetRows = Math.min(Math.max(calculatedRows, minRows), maxRows);
    const targetHeight = targetRows * lineHeight;

    // Set height
    textarea.style.height = `${targetHeight}px`;

    // Enable/disable scrolling based on whether we're at max rows
    if (calculatedRows > maxRows) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  };

  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value]);

  // Adjust height when input value changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue]);

  // Set initial height on component mount
  useEffect(() => {
    if (textareaRef.current) {
      adjustTextareaHeight();
    }
  }, []);

  useEffect(() => {
    if (showSuggestions) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      const filtered = (questions || [])
        .filter(q => q.text.toLowerCase().includes(lowerSearchTerm))
        .slice(0, 7);
      setFilteredQuestions(filtered);
      setActiveMentionIndex(filtered.length > 0 ? 0 : -1);
    } else {
      setFilteredQuestions([]);
      setActiveMentionIndex(-1);
    }
  }, [searchTerm, questions, showSuggestions]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        textareaRef.current &&
        !textareaRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check if cursor is inside a JSON string
  const isInsideJsonString = (text, cursorPos) => {
    const beforeCursor = text.substring(0, cursorPos);
    // Count unescaped quotes before cursor
    const quotesBeforeCursor = (beforeCursor.match(/(?<!\\)"/g) || []).length;
    return quotesBeforeCursor % 2 === 1;
  };

  const handleInputChange = (e) => {
    const text = e.target.value;
    const cursorPos = e.target.selectionStart;
    setInputValue(text);

    const syntheticEvent = { target: { value: text, name: name || '' } };
    onChange(syntheticEvent);

    // Trigger auto-resize after input change
    setTimeout(() => adjustTextareaHeight(), 0);

    const textUpToCursor = text.substring(0, cursorPos);
    const atMatch = textUpToCursor.match(/@(\w*)$/);

    if (atMatch) {
      const atIndex = textUpToCursor.lastIndexOf('@');
      if (atIndex === 0 || (atIndex > 0 && /\s/.test(text[atIndex - 1]))) {
        setSearchTerm(atMatch[1]);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (question) => {
    const currentText = inputValue;
    const cursorPos = textareaRef.current?.selectionStart || currentText.length;

    const textBeforeCursor = currentText.substring(0, cursorPos);
    const atCharPos = textBeforeCursor.lastIndexOf('@');

    if (atCharPos !== -1) {
      if (atCharPos === 0 || (atCharPos > 0 && /\s/.test(currentText[atCharPos - 1]))) {
        const isInString = isInsideJsonString(currentText, atCharPos);
        let mentionText;

        if (isInString) {
          mentionText = `{{${question.text}}} `;
        } else {
          mentionText = `"{{${question.text}}}" `;
        }

        const newText =
          currentText.substring(0, atCharPos) +
          mentionText +
          currentText.substring(cursorPos);

        setInputValue(newText);
        const syntheticEvent = { target: { value: newText, name: name || '' } };
        onChange(syntheticEvent);
        setShowSuggestions(false);

        setTimeout(() => {
          textareaRef.current?.focus();
          const newCursorPos = atCharPos + mentionText.length;
          textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
          // Trigger auto-resize after mention insertion
          adjustTextareaHeight();
        }, 0);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e) => {
    // Handle Tab key for indentation (when suggestions are not shown)
    if (e.key === 'Tab' && !showSuggestions) {
      e.preventDefault();
      const textarea = textareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentText = inputValue;

        // Insert tab character at cursor position
        const newText = currentText.substring(0, start) + '\t' + currentText.substring(end);

        setInputValue(newText);
        const syntheticEvent = { target: { value: newText, name: name || '' } };
        onChange(syntheticEvent);

        // Move cursor after the inserted tab
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + 1, start + 1);
          // Trigger auto-resize after tab insertion
          adjustTextareaHeight();
        }, 0);
      }
      return;
    }

    if (e.key === 'Backspace' && !showSuggestions) {
      const textarea = textareaRef.current;
      if (textarea && textarea.selectionStart > 0 && textarea.selectionStart === textarea.selectionEnd) {
        const currentText = inputValue;
        const cursorPos = textarea.selectionStart;

        if (currentText[cursorPos - 1] === ' ') {
          for (const question of (questions || [])) {
            const quotedMention = `"{{${question.text}}}" `;
            const unquotedMention = `{{${question.text}}} `;

            if (cursorPos >= quotedMention.length && 
                currentText.substring(cursorPos - quotedMention.length, cursorPos) === quotedMention) {
              e.preventDefault();
              const newText = currentText.substring(0, cursorPos - quotedMention.length) + 
                            currentText.substring(cursorPos);
              setInputValue(newText);
              const syntheticEvent = { target: { value: newText, name: name || '' } };
              onChange(syntheticEvent);
              setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(cursorPos - quotedMention.length, cursorPos - quotedMention.length);
              }, 0);
              return;
            } else if (cursorPos >= unquotedMention.length && 
                      currentText.substring(cursorPos - unquotedMention.length, cursorPos) === unquotedMention) {
              e.preventDefault();
              const newText = currentText.substring(0, cursorPos - unquotedMention.length) + 
                            currentText.substring(cursorPos);
              setInputValue(newText);
              const syntheticEvent = { target: { value: newText, name: name || '' } };
              onChange(syntheticEvent);
              setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(cursorPos - unquotedMention.length, cursorPos - unquotedMention.length);
              }, 0);
              return;
            }
          }
        }
      }
    }

    if (showSuggestions && filteredQuestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveMentionIndex(prev => (prev + 1) % filteredQuestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveMentionIndex(prev => (prev - 1 + filteredQuestions.length) % filteredQuestions.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        if (activeMentionIndex > -1 && filteredQuestions[activeMentionIndex]) {
          e.preventDefault();
          handleSuggestionClick(filteredQuestions[activeMentionIndex]);
        } else {
          setShowSuggestions(false);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowSuggestions(false);
      }
    }
  };

  return (
    <div className="relative w-full">
      <Textarea
        ref={textareaRef}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        name={name}
        {...props}
      />
      {showSuggestions && filteredQuestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-[10002] w-full bg-white border border-gray-200 rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto"
        >
          <ul>
            {filteredQuestions.map((q, index) => (
              <li
                key={q.id}
                className={cn(
                  "px-3 py-1.5 text-sm hover:bg-gray-100 cursor-pointer",
                  index === activeMentionIndex && "bg-gray-100"
                )}
                onClick={() => handleSuggestionClick(q)}
                onMouseEnter={() => setActiveMentionIndex(index)}
              >
                {q.text}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default JsonMentionTextarea; 